import React, { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import type { Project, Task } from '../types';
import { LocalProvider } from '../providers/LocalProvider.ts';
import { uid } from '../utils/id.ts';

interface TaskContextType {
  projects: Project[];
  tasks: Task[];
  currentTaskId: string | null;
  createProject: (name: string) => void;
  updateProject: (id: string, changes: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  createTask: (text: string, projectId?: string) => void;
  updateTask: (id: string, changes: Partial<Task>) => void;
  deleteTask: (id: string) => void;
  setCurrentTaskId: (id: string | null) => void;
  addTaskTime: (id: string, duration: number) => void;
  completeTask: (id: string) => void;
  uncompleteTask: (id: string) => void;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export const TaskContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>(() => LocalProvider.loadProjects());
  const [tasks, setTasks] = useState<Task[]>(() => LocalProvider.loadTasks());
  const [currentTaskId, setCurrentTaskIdState] = useState<string | null>(
    () => LocalProvider.loadCurrentTaskId()
  );

  const createProject = useCallback((name: string) => {
    setProjects(prev => {
      const project: Project = {
        id: uid(),
        name,
        collapsed: false,
        sortOrder: prev.length,
        createdAt: Date.now(),
      };
      const updated = [...prev, project];
      LocalProvider.saveProjects(updated);
      return updated;
    });
  }, []);

  const updateProject = useCallback((id: string, changes: Partial<Project>) => {
    setProjects(prev => {
      const updated = prev.map(p => p.id === id ? { ...p, ...changes } : p);
      LocalProvider.saveProjects(updated);
      return updated;
    });
  }, []);

  const deleteProject = useCallback((id: string) => {
    setProjects(prev => {
      const updated = prev.filter(p => p.id !== id);
      LocalProvider.saveProjects(updated);
      return updated;
    });
    setTasks(prev => {
      const updated = prev.filter(t => t.projectId !== id);
      LocalProvider.saveTasks(updated);
      return updated;
    });
    setCurrentTaskIdState(prev => {
      // Will be reconciled after tasks update — clear if needed via separate check
      return prev;
    });
  }, []);

  const createTask = useCallback((text: string, projectId?: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'task_created', {
        event_category: 'tasks',
        has_project: !!projectId,
      });
    }
    setTasks(prev => {
      const task: Task = {
        id: uid(),
        text,
        projectId,
        completed: false,
        sortOrder: prev.length,
        createdAt: Date.now(),
        totalTimeSpent: 0,
      };
      const updated = [...prev, task];
      LocalProvider.saveTasks(updated);
      return updated;
    });
  }, []);

  const updateTask = useCallback((id: string, changes: Partial<Task>) => {
    setTasks(prev => {
      const updated = prev.map(t => t.id === id ? { ...t, ...changes } : t);
      LocalProvider.saveTasks(updated);
      return updated;
    });
  }, []);

  const deleteTask = useCallback((id: string) => {
    setTasks(prev => {
      const updated = prev.filter(t => t.id !== id);
      LocalProvider.saveTasks(updated);
      return updated;
    });
    setCurrentTaskIdState(prev => (prev === id ? null : prev));
  }, []);

  const setCurrentTaskId = useCallback((id: string | null) => {
    LocalProvider.saveCurrentTaskId(id);
    setCurrentTaskIdState(id);
  }, []);

  const completeTask = useCallback((id: string) => {
    if (typeof window.gtag !== 'undefined') {
      const task = tasks.find(t => t.id === id);
      window.gtag('event', 'task_completed', { total_time_spent_sec: task?.totalTimeSpent ?? 0 });
    }
    setTasks(prev => {
      const updated = prev.map(t =>
        t.id === id ? { ...t, completed: true, completedAt: Date.now() } : t
      );
      LocalProvider.saveTasks(updated);
      return updated;
    });
    setCurrentTaskIdState(prev => (prev === id ? null : prev));
    LocalProvider.saveCurrentTaskId(currentTaskId === id ? null : currentTaskId);
  }, [tasks, currentTaskId]);

  const uncompleteTask = useCallback((id: string) => {
    setTasks(prev => {
      const updated = prev.map(t =>
        t.id === id ? { ...t, completed: false, completedAt: undefined } : t
      );
      LocalProvider.saveTasks(updated);
      return updated;
    });
  }, []);

  const addTaskTime = useCallback((id: string, duration: number) => {
    setTasks(prev => {
      const updated = prev.map(t =>
        t.id === id ? { ...t, totalTimeSpent: t.totalTimeSpent + duration } : t
      );
      LocalProvider.saveTasks(updated);
      return updated;
    });
  }, []);

  return (
    <TaskContext.Provider value={{
      projects,
      tasks,
      currentTaskId,
      createProject,
      updateProject,
      deleteProject,
      createTask,
      updateTask,
      deleteTask,
      setCurrentTaskId,
      addTaskTime,
      completeTask,
      uncompleteTask,
    }}>
      {children}
    </TaskContext.Provider>
  );
};

export const useTask = (): TaskContextType => {
  const ctx = useContext(TaskContext);
  if (!ctx) throw new Error('useTask must be used within a TaskContextProvider');
  return ctx;
};
